/**
 * The result a tool produces in memory, before serialization.
 * The ok/error distinction is the fact analytics needs, so it lives here — at the type —
 * rather than being implied by the serialized body.
 */
export type WebMcpToolOutcome = { ok: true; data: unknown } | { ok: false; message: string };

/**
 * Payload for the FullStory `webmcp_tool_executed` event. Tool args are deliberately never
 * included — several WebMCP tools accept PII (email, phone, DOB).
 */
export type WebMcpToolExecutedPayload = {
  webmcp_tool_name: string;
  webmcp_tool_success: boolean;
  webmcp_tool_duration_ms: number;
};

/** The wire format handed to the browser's WebMCP implementation (the spec's tool result). */
export interface WebMcpToolResult {
  content: { type: 'text'; text: string }[];
}

export interface WebMcpTool extends ModelContextTool {
  execute: (args: Record<string, unknown>, options?: ToolExecuteCallbackOptions) => Promise<WebMcpToolOutcome> | WebMcpToolOutcome;
}

export const okOutcome = (data: unknown): WebMcpToolOutcome => ({ ok: true, data });

export const errorOutcome = (message: string): WebMcpToolOutcome => ({ ok: false, message });

/**
 * Serializes an outcome to the agent-facing wire format at a single boundary:
 * success payloads pretty-printed, errors as `{"error": message}` so the agent can self-correct.
 */
export const toWireResult = (outcome: WebMcpToolOutcome): WebMcpToolResult =>
  // `?? null`: JSON.stringify(undefined) returns undefined, which would put `text: undefined` on the
  // wire — an ok outcome with no payload serializes as JSON `null` instead.
  outcome.ok ? { content: [{ type: 'text', text: JSON.stringify(outcome.data ?? null, null, 2) }] } : { content: [{ type: 'text', text: JSON.stringify({ error: outcome.message }) }] };

/** Formats an HttpErrorResponse (or anything else) for an agent, including the API error body so it can self-correct. */
export const describeError = (err: unknown): string => {
  const e = err as { message?: string; error?: unknown } | undefined;
  const body = e?.error ? ` — ${JSON.stringify(e.error)}` : '';
  return `${e?.message ?? 'unknown error'}${body}`;
};
